import { Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';

import { Job } from '../../../domain/models/job';
import { RouteDetailService } from '../../services/route-detail.service';
import { MonthViewModel } from '../../viewModels/month-viewmodel';
import { SelectedDay } from '../../viewModels/selectedDay-viewmodel';

@Component({
  selector: 'app-all-routes-selector',
  templateUrl: './all-routes-selector.component.html',
  styleUrls: ['./all-routes-selector.component.scss']
})
export class AllRoutesSelectorComponent implements OnInit {
  @ViewChildren('jobElementRef') jobElements: QueryList<ElementRef>;

  @Input() public chosenJobId: string;
  @Input() public initialSelectedJobId: string;
  @Input() public initialSelectedDates: Date[];
  @Output() public onConfirm: EventEmitter<{ job, dates }>;
  @Output() public onCancel: EventEmitter<void>;

  private routes: Job[];
  private datesShown: boolean;
  private jobsShown: boolean;

  public includeArchived: boolean;
  public includeProjects: boolean;
  public includeShared: boolean;
  public filter: string;
  public filteredJobs: Job[];
  public months: MonthViewModel[];
  public selectedJob: Job;
  public selectableDates: SelectedDay[];
  public selectedDates: Date[];

  private readonly maxItemsAllowed = 5;
  public maxPossibleItems: number;
  public itemsSelected: number;

  constructor(private routeDetailService: RouteDetailService) {
    this.onConfirm = new EventEmitter<{ job, dates }>();
    this.onCancel = new EventEmitter<void>();
    this.months = [];
  }

  ngOnInit() {
    this.routes = [];
    this.datesShown = false;
    this.jobsShown = true;
    this.getAllJobs();
  }

  getDayName(date: Date): string {
    return moment(date).format('dddd').toUpperCase();
  }

  getAllJobs(): void {
    this.routeDetailService.getAllUnfilterdJobs().subscribe((result: Job[]) => {
      this.routes = result.filter((route: Job) => route.id !== this.chosenJobId);
      this.clearFilter();

      if (!this.initialSelectedJobId) { return; }

      let job = _.find(this.routes, r => r.id === this.initialSelectedJobId);
      if (!job) { return; }

      this.selectJob(job);
      if (!this.selectableDates) { return; }

      _.each(this.initialSelectedDates, (selectedDate: Date) => {
        let date = _.find(this.selectableDates, d => d.date.getTime() === selectedDate.getTime());
        if (date) {
          date.selected = true;
        }
      });

      this.setItemsSelected();
    });
  }

  filterRoutes(): void {
    this.filteredJobs = this.routes;

    if (this.filter != null && this.filter !== undefined && this.filter.length > 0) {
      this.filteredJobs = this.filteredJobs.filter((job: Job) => { return job.name.toUpperCase().includes(this.filter.toUpperCase()); });
    }
    if (!this.includeArchived) {
      this.filteredJobs = this.filteredJobs.filter((job: Job) => {
        return job.archived === false;
      });
    }
    if (!this.includeProjects) {
      this.filteredJobs = this.filteredJobs.filter((job: Job) => {
        return job.projectId === undefined || job.projectId == null;
      });
    }
  }

  clearFilter(): void {
    this.filter = '';
    this.filteredJobs = this.routes;
  }

  selectJob(job: Job): void {
    this.datesShown = false;
    this.selectedJob = job;
    this.months = [];
    this.jobsShown = false;
    this.setDates();
    this.initializeMonths();
  }

  scrollJobIntoView(): void {
    const jobElement: HTMLElement = this.jobElements.find((element: ElementRef) => element.nativeElement.id === this.selectedJob.id).nativeElement;
    jobElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }

  isSelected(job: Job): boolean {
    return this.selectedJob !== undefined && this.selectedJob.id === job.id;
  }

  showDates(): boolean {
    return this.datesShown;
  }

  showJobs(): boolean {
    return this.jobsShown;
  }

  toggleJobs(): void {
    this.jobsShown = true;
    this.datesShown = false;
    this.resetItemsSelected();
    setTimeout(() => {
      this.scrollJobIntoView();
    });
  }

  confirm(): void {
    let dates = this.selectableDates.filter((dateObj: any) => {
      return dateObj.selected;
    });

    this.onConfirm.emit({ job: this.selectedJob, dates: dates.map(d => d.date) });
  }

  cancel(): void {
    this.onCancel.emit();
  }

  hasAvailableDates(): boolean {
    return this.selectableDates.length > 0;
  }

  hasItemsSelected(): boolean {
    if (this.selectedJob !== undefined) {
      let selected = this.selectableDates.filter((date: any) => {
        return date.selected;
      });
      return selected.length > 0 && selected.length <= 5;
    }
    return false;
  }

  resetItemsSelected(): void {
    if (this.selectedJob !== undefined) {
      this.selectableDates.forEach((date: any) => {
        date.selected = false;
      });
    }
  }

  setItemsSelected(): void {
    this.itemsSelected = _.filter(this.selectableDates, (date: SelectedDay) => date.selected).length;
  }

  isDisabled(date: SelectedDay): boolean {
    return date && this.itemsSelected >= this.maxItemsAllowed && !date.selected;
  }

  private setDates(): void {
    let dates: SelectedDay[] = [];
    let stop = new Date(this.selectedJob.end);
    let start = new Date(this.selectedJob.start);
    if (stop > new Date()) {
      stop = new Date();
    }
    stop.setHours(0, 0, 0, 0);

    start.setHours(0, 0, 0, 0);
    stop.setHours(0, 0, 0, 0);
    for (let i: Date = new Date(start); i <= stop; i.setDate(i.getDate() + 1)) {
      if (_.includes(this.selectedJob.schedule.days, i.getDay())) {
        dates.push(new SelectedDay(new Date(i), false));
      }
    }

    this.itemsSelected = 0;
    this.maxPossibleItems = (dates.length > this.maxItemsAllowed) ? this.maxItemsAllowed : dates.length;
    this.selectableDates = dates;
    this.datesShown = true;
  }

  private initializeMonths(): void {
    for (let i = 0; i < 12; i++) {
      const daysInMonth = _.filter(this.selectableDates, (date: SelectedDay) => {
        return date.date.getMonth() === i;
      });
      const mostRecentDate: Date = this.hasAvailableDates() ? this.selectableDates[this.selectableDates.length - 1].date : new Date();
      this.months.push({
        index: i,
        name: moment().month(i).format('MMMM').toUpperCase(),
        selected: _.some(daysInMonth, (day: SelectedDay) => {
          return day.selected || mostRecentDate.getMonth() === i;
        }),
        disabled: daysInMonth.length === 0
      });
    }
  }

  private getSelectedMonths(): MonthViewModel[] {
    return _.filter(this.months, (month: MonthViewModel) => {
      return month.selected;
    });
  }

  toggleMonth(month: MonthViewModel): void {
    if (!month.selected || this.getSelectedMonths().length > 1) {
      month.selected = !month.selected;
    }
  }

  getAvailableDates(): SelectedDay[] {
    const selectedMonths: MonthViewModel[] = this.getSelectedMonths();
    const availableDates = _.filter(this.selectableDates, (day: SelectedDay) => {
      return (_.some(selectedMonths, (month: MonthViewModel) => {
        return day.date.getMonth() === month.index;
      }));
    });
    return availableDates;
  }
}




// WEBPACK FOOTER //
// ./src/app/modules/route-detail/components/all-routes-selector/all-routes-selector.component.ts